import { AlertCircle, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useScenarios } from '@/hooks/useScenarios'
import { formatMapId } from '@/common/routeUtil'
import type { MarkValidationError } from '@/common/markUtil'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { ENABLE_CARGO_TOGGLE } from '@/config/scenarioConfig'

interface RouteEditSidebarProps {
  s: ReturnType<typeof useScenarios>
}

// ルート編集・新規作成モードのサイドバー。マーク一覧の表示・選択を担う。
// マーク追加 / Undo / Redo / キャンセル / 保存はマップ上のフローティングツールバー、
// 選択マークの編集（座標 / 向き / 走行方向 / 荷役設定）はマップ右側のフローティングパネル。
export default function RouteEditSidebar({ s }: RouteEditSidebarProps) {
  const { t } = useTranslation()
  const re = s.routeEdit
  const route = re.editingRoute
  const isCreating = re.isCreatingRoute
  if (!re.isActive) return null

  const marks = re.marks
  const sel = re.selectedMarkIndex
  const saveLabel = isCreating
    ? t('scenarios.register')
    : t('scenarios.routeEditSave')

  const markErrorText = (error: MarkValidationError): string => {
    switch (error.kind) {
      case 'cargoNotEndpoint':
        return t('scenarios.markCargoNotEndpointError')
      case 'cargoStartNotForward':
        return t('scenarios.markCargoStartNotForwardError')
      case 'cargoEndNotReverse':
        return t('scenarios.markCargoEndNotReverseError')
      case 'cargoNeedsIntermediate':
        return t('scenarios.markCargoNeedsIntermediateError')
      default:
        return t('scenarios.markTooCloseError', {
          distance: error.distance.toFixed(2),
          min: error.minDistance,
        })
    }
  }

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* ヘッダ */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-800">
          {isCreating
            ? t('scenarios.routeCreateTitle')
            : t('scenarios.routeEditTitle')}
        </h3>
        {route && (
          <span className="text-xs text-gray-500">
            {formatMapId(route.mapId)}
          </span>
        )}
      </div>

      {/* 操作方法・再記録方式の注意 */}
      <div className="px-3 py-2 border-b text-[11px] text-gray-500 leading-snug">
        {isCreating
          ? t('scenarios.routeCreateNote')
          : t('scenarios.routeEditNote')}
      </div>

      {/* マーク一覧（マーク追加 / Undo / Redo はマップ上のフローティングツールバー） */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="text-xs text-gray-500 mb-1">
          {t('scenarios.marks')} ({marks.length})
        </div>
        {marks.length === 0 && (
          <div className="text-gray-400 text-center py-6 text-xs">
            {t('scenarios.addMarkHint')}
          </div>
        )}
        <ul className="flex flex-col gap-1 select-none">
          {marks.map((mark, index) => {
            const isSelected = sel === index
            // このマークに対応するエラー（アイテム内にメッセージを表示する）
            const errors = re.markErrors.filter((e) => e.index === index)
            const hasError = errors.length > 0
            return (
              <li
                key={index}
                onClick={() => re.selectMark(isSelected ? null : index)}
                className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-orange-50 border-orange-400'
                    : hasError
                      ? 'bg-red-50 border-red-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="w-5 text-center text-xs font-bold text-gray-600">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0 text-[11px] text-gray-600">
                  <div className="flex items-center gap-1">
                    {ENABLE_CARGO_TOGGLE && mark.isCargo && (
                      <span className="px-1 py-0.5 text-[10px] font-bold text-white bg-orange-500 rounded leading-none">
                        高
                      </span>
                    )}
                    {index > 0 && mark.reverse && (
                      <span className="px-1 py-0.5 text-[10px] font-bold text-white bg-purple-500 rounded leading-none">
                        {t('scenarios.reverse')}
                      </span>
                    )}
                    <span className="tabular-nums truncate">
                      ({mark.x.toFixed(2)}, {mark.y.toFixed(2)})
                    </span>
                  </div>
                  {ENABLE_CARGO_TOGGLE && mark.isCargo && (
                    <div className="text-orange-600">
                      {Math.floor(
                        Math.max(mark.height, 0) * 1000
                      ).toLocaleString()}
                      mm
                    </div>
                  )}
                  {errors.map((error) => (
                    <div
                      key={`mark-error-${error.kind}`}
                      className="mt-0.5 text-[10px] text-red-600 leading-snug"
                    >
                      {markErrorText(error)}
                    </div>
                  ))}
                </div>
                {hasError && (
                  <AlertCircle
                    size={14}
                    className="text-red-500 flex-shrink-0"
                  />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    re.deleteMark(index)
                  }}
                  className="text-gray-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="delete-mark"
                  title={t('scenarios.deleteMark')}
                >
                  <Trash2 size={14} />
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      {/* マーク数が足りない間の案内 */}
      {marks.length < 2 && (
        <div className="px-3 py-1.5 border-t text-[11px] text-gray-400">
          {t('scenarios.routeMarksMinNote')}
        </div>
      )}

      {/* ダイアログ（キャンセル / 保存ボタンはマップ上のツールバー側） */}
      <ConfirmDialog
        isOpen={re.showSaveConfirm}
        title={
          isCreating
            ? t('scenarios.routeCreateSaveTitle')
            : t('scenarios.routeEditSaveTitle')
        }
        message={
          <>
            <p>
              {isCreating
                ? t('scenarios.routeCreateSaveConfirm')
                : t('scenarios.routeEditSaveConfirm')}
            </p>
            {!isCreating && (
              <p className="mt-2 text-sm text-gray-500">
                {t('scenarios.routeEditRecreateNote')}
              </p>
            )}
            {!isCreating && re.usedScenarioCount > 0 && (
              <p className="mt-2 text-sm text-orange-600">
                {t('scenarios.routeEditSaveScenarioNote', {
                  count: re.usedScenarioCount,
                })}
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500">
              {t('scenarios.deviceUpdateNote')}
            </p>
          </>
        }
        confirmLabel={
          re.isSavingRoute ? t('scenarios.routeEditSaving') : saveLabel
        }
        confirmDisabled={re.isSavingRoute}
        onConfirm={re.confirmSave}
        onCancel={re.cancelSave}
      />
      <ConfirmDialog
        isOpen={re.showCancelConfirm}
        title={t('scenarios.routeEditDiscardTitle')}
        message={t('scenarios.routeEditDiscardMessage')}
        confirmLabel={t('scenarios.discard')}
        isAlert={true}
        onConfirm={re.confirmCancel}
        onCancel={re.dismissCancel}
      />
    </div>
  )
}
